import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 md:pt-20 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
          <Link
            to="/articles"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回文章列表
          </Link>
        </div>
        <Footer />
        <ScrollToTop />
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '鲍鱼百科', href: '/articles' },
    { label: article.title },
  ];

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.coverImageUrl,
    publisher: {
      '@type': 'Organization',
      name: '鲍鱼盒子',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16 md:pt-20">
          <Breadcrumb items={breadcrumbItems} />

          <article className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-3xl mx-auto">
              {/* Article Header */}
              <header className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="text-xs text-foreground-400">{article.readTime}</span>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-user-line text-sm"></i>
                  </div>
                  <span className="text-sm text-foreground-600 font-medium">{article.author}</span>
                </div>
              </header>

              {/* Cover Image */}
              <div className="w-full aspect-[2/1] rounded-xl overflow-hidden bg-background-100 mb-8 md:mb-10">
                <img
                  src={article.coverImageUrl}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200">
                <span className="text-xs text-foreground-400">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-foreground-600 bg-background-100 px-3 py-1.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Back Link */}
              <div className="mt-8">
                <Link
                  to="/articles"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回文章列表
                </Link>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 md:mb-10">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.id}
                    to={`/articles/${related.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200 overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
                  >
                    <div className="w-full aspect-[4/3] overflow-hidden bg-background-100">
                      <img
                        src={related.coverImageUrl}
                        alt={related.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full">
                          {related.category}
                        </span>
                        <span className="text-xs text-foreground-400">{related.date}</span>
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </div>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}